import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Box,
  Drawer,
  Toolbar,
  List,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/DashboardOutlined";
import CalendarIcon from "@mui/icons-material/CalendarMonthOutlined";
import BikeScooterIcon from "@mui/icons-material/BikeScooterOutlined";
import SubscriptionsIcon from "@mui/icons-material/AutorenewOutlined";
import CardMembershipIcon from "@mui/icons-material/CardMembershipOutlined";
import PeopleIcon from "@mui/icons-material/PeopleOutline";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccountOutlined";
import InsightsIcon from "@mui/icons-material/InsightsOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import StockGroupsIcon from "@mui/icons-material/AllInboxOutlined";
import RestaurantIcon from "@mui/icons-material/RestaurantOutlined";
import LocalCafeIcon from "@mui/icons-material/LocalCafeOutlined";
import { canViewDrinks, canViewInstantMenu } from "../../utils/permissions";
import LocationOnIcon from "@mui/icons-material/LocationOnOutlined";
import MapIcon from "@mui/icons-material/MapOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHorizOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusyOutlined";
import StarIcon from "@mui/icons-material/StarBorder";
import InventoryIcon from "@mui/icons-material/Inventory2Outlined";
import IngredientsIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import BoltIcon from "@mui/icons-material/BoltOutlined";
import CategoryIcon from "@mui/icons-material/CategoryOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import HistoryIcon from "@mui/icons-material/HistoryOutlined";
import CombosIcon from "@mui/icons-material/WidgetsOutlined";
import SideItemsIcon from "@mui/icons-material/PostAddOutlined";
import FeedbackIcon from "@mui/icons-material/ForumOutlined";
import RiderRequestsIcon from "@mui/icons-material/PersonAddOutlined";
import KitchenIcon from "@mui/icons-material/StorefrontOutlined";
import MenuIcon from "@mui/icons-material/RestaurantMenuOutlined";
import BusinessIcon from "@mui/icons-material/BusinessCenterOutlined";
import AdministrationIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import useAuth from "../../hooks/useAuth";

interface NavigationGroup {
  id: string;
  label: string;
  paths: string[];
  children?: NavigationGroup[];
}

export const NAVIGATION_GROUPS: NavigationGroup[] = [
  { id: "kitchen", label: "Kitchen Operations", paths: ["/instant-orders", "/instant-orders/history", "/now-meals", "/orders", "/kitchen-requirements", "/live-map"] },
  { id: "menu", label: "Menu & Catalog", paths: ["/meals", "/drinks", "/now-combos", "/swap-sets"], children: [
    { id: "catalog-settings", label: "Catalog Settings", paths: ["/item-categories", "/shared-stock-groups", "/discovery-badges", "/side-items"] },
  ] },
  { id: "customers", label: "Customers", paths: ["/users", "/subscriptions", "/feedbacks"] },
  { id: "business", label: "Business", paths: ["/dashboard", "/analytics", "/plans", "/discounts"] },
  { id: "administration", label: "Administration", paths: ["/kitchens", "/stocks", "/deliveryAgents", "/rider-requests", "/service-areas-map", "/requested-areas", "/holidays"], children: [
    { id: "staff", label: "Staff", paths: ["/admins", "/payslip-generator"] },
  ] },
];

const NAVIGATION_LABELS: Record<string, string> = {
  "/drinks": "Drinks", "/instant-orders/history": "Order History", "/plans": "Plans",
  "/feedbacks": "Feedback", "/side-items": "Side Items", "/service-areas-map": "Service Areas",
  "/admins": "Staff Management", "/payslip-generator": "Payslips", "/holidays": "Holidays",
};

const SECTION_ICONS: Record<string, React.ReactNode> = {
  kitchen: <KitchenIcon />,
  menu: <MenuIcon />,
  customers: <PeopleIcon />,
  business: <BusinessIcon />,
  administration: <AdministrationIcon />,
};

interface SidebarProps {
  drawerWidth: number;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  collapsed?: boolean;
}

const getActiveSections = (pathname: string): Record<string, boolean> => {
  const sections: Record<string, boolean> = {};
  const matches = (path: string) => pathname === path || pathname.startsWith(`${path}/`);
  for (const group of NAVIGATION_GROUPS) {
    const paths = [...group.paths, ...(group.children || []).flatMap(child => child.paths)];
    if (paths.some(matches)) sections[group.id] = true;
    for (const child of group.children || []) {
      if (child.paths.some(matches)) sections[child.id] = true;
    }
  }
  return sections;
};

const Sidebar: React.FC<SidebarProps> = ({
  drawerWidth,
  mobileOpen = false,
  onMobileClose,
  collapsed = false,
}) => {
  const location = useLocation();
  const { admin } = useAuth();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => ({
    kitchen: true,
    ...getActiveSections(location.pathname),
  }));
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  useEffect(() => {
    setExpanded(previous => ({ ...previous, ...getActiveSections(location.pathname) }));
  }, [location.pathname]);

  const menuItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/dashboard",
      permission: "analytics",
    },
    {
      text: "Subscription Orders",
      icon: <CalendarIcon />,
      path: "/orders",
      permission: "orders",
    },
    {
      text: "Ingredients",
      icon: <IngredientsIcon />,
      path: "/kitchen-requirements",
      permission: "orders",
    },
    {
      text: "Subscriptions",
      icon: <SubscriptionsIcon />,
      path: "/subscriptions",
      permission: "subscriptions",
    },
    {
      text: "Plans Management",
      icon: <CardMembershipIcon />,
      path: "/plans",
      permission: "subscriptions",
    },
    {
      text: "Users",
      icon: <PeopleIcon />,
      path: "/users",
      permission: "users",
    },
    {
      text: "Delivery Agents",
      icon: <BikeScooterIcon />,
      path: "/deliveryAgents",
      permission: "deliveryAgents",
    },
    {
      text: "Rider Requests",
      icon: <RiderRequestsIcon />,
      path: "/rider-requests",
      permission: "deliveryAgents",
    },
    {
      text: "Live Map",
      icon: <MapIcon />,
      path: "/live-map",
      permission: "deliveryAgents",
    },
    {
      text: "Discounts & Offers",
      icon: <LocalOfferIcon />,
      path: "/discounts",
      permission: "discounts",
    },
    {
      text: "Meals",
      icon: <RestaurantIcon />,
      path: "/meals",
      permission: "meals",
    },
    {
      text: "Drinks (Coffees & Blends)",
      icon: <LocalCafeIcon />,
      path: "/drinks",
      permission: "drinks",
    },
    {
      text: "Instant Meals",
      icon: <BoltIcon />,
      path: "/now-meals",
      permission: "nowMeals",
    },
    {
      text: "Instant Combos",
      icon: <CombosIcon />,
      path: "/now-combos",
      permission: "nowMeals",
    },
    {
      text: "Item Categories",
      icon: <CategoryIcon />,
      path: "/item-categories",
      permission: "nowMeals",
    },
    {
      text: "Shared Stock Groups",
      icon: <StockGroupsIcon />,
      path: "/shared-stock-groups",
      permission: "nowMeals",
    },
    {
      text: "Discovery Badges",
      icon: <StarIcon />,
      path: "/discovery-badges",
      permission: "nowMeals",
    },
    {
      text: "Side Items (Add-ons)",
      icon: <SideItemsIcon />,
      path: "/side-items",
      permission: "nowMeals",
    },
    {
      text: "Live Orders",
      icon: <ReceiptLongIcon />,
      path: "/instant-orders",
      permission: "instantOrders",
    },
    {
      text: "Instant Orders History",
      icon: <HistoryIcon />,
      path: "/instant-orders/history",
      permission: "instantOrders",
    },
    {
      text: "Swap Sets",
      icon: <SwapHorizIcon />,
      path: "/swap-sets",
      permission: "swapSets",
    },
    {
      text: "Feedbacks",
      icon: <FeedbackIcon />,
      path: "/feedbacks",
      permission: "orders",
    },
    {
      text: "Requested Areas",
      icon: <LocationOnIcon />,
      path: "/requested-areas",
      permission: "analytics",
    },
    {
      text: "Service Areas Map",
      icon: <MapIcon />,
      path: "/service-areas-map",
      permission: "analytics",
    },
    {
      text: "Analytics",
      icon: <InsightsIcon />,
      path: "/analytics",
      permission: "analytics",
    },
    {
      text: "Staff Management",
      icon: <SupervisorAccountIcon />,
      path: "/admins",
      permission: "admins",
    },
    {
      text: "Payslip Generator",
      icon: <PaymentsIcon />,
      path: "/payslip-generator",
      permission: "admins",
    },
    {
      text: "Holiday management",
      icon: <EventBusyIcon />,
      path: "/holidays",
      permission: "holidays",
    },
    {
      text: "Kitchens",
      icon: <KitchenIcon />,
      path: "/kitchens",
      permission: "kitchens",
    },
    {
      text: "Stock Management",
      icon: <InventoryIcon />,
      path: "/stocks",
      permission: "kitchens",
    },
  ];

  const visibleItems = menuItems.filter(item => item.path === "/now-meals" ? canViewInstantMenu(admin) : item.permission === "drinks" ? canViewDrinks(admin) :
    admin?.role === "superadmin" || admin?.permissions?.[item.permission] === true);
  const activePath = visibleItems
    .filter(item => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;
  const getItems = (paths: string[]) => paths.flatMap(path => visibleItems.filter(item => item.path === path));
  const toggleGroup = (id: string) => setExpanded(previous => ({ ...previous, [id]: !previous[id] }));

  const renderDrawer = (isMobile: boolean) => {
    const isCollapsed = collapsed && !isMobile;
    const renderItem = (item: typeof menuItems[number], nested = false) => {
      const selected = activePath === item.path;
      const label = NAVIGATION_LABELS[item.path] || item.text;
      return (
        <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
          <Tooltip title={isCollapsed ? label : ""} placement="right" arrow>
            <ListItemButton component={Link} to={item.path} selected={selected}
              aria-label={label} aria-current={selected ? "page" : undefined}
              onClick={() => { if (isMobile) onMobileClose?.(); }}
              sx={{ mx: isCollapsed ? 1 : 1.5, px: isCollapsed ? 0 : nested ? 2.5 : 1.5,
                minHeight: isCollapsed ? 40 : 48, py: 1, borderRadius: "6px", justifyContent: isCollapsed ? "center" : "flex-start",
                color: "#475467", position: "relative", transition: reduceMotion ? "none" : "background-color 140ms, color 140ms",
                "&.Mui-selected": { bgcolor: "#fff1e7", color: "#b54708", "&:hover": { bgcolor: "#ffe8d6" },
                  "&::before": { content: '""', position: "absolute", left: 0, top: 9, bottom: 9, width: 3, bgcolor: "#f97316", borderRadius: "2px" } },
                "&:hover": { bgcolor: "#f5f6f8" }, "&.Mui-focusVisible": { outline: "2px solid #f97316", outlineOffset: -2 } }}>
              <ListItemIcon sx={{ color: selected ? "#e56b12" : "#667085", minWidth: isCollapsed ? 0 : 32, justifyContent: "center", mr: isCollapsed ? 0 : 0.5, "& .MuiSvgIcon-root": { fontSize: 22 } }}>{item.icon}</ListItemIcon>
              {!isCollapsed && <ListItemText primary={label} sx={{ my: 0, minWidth: 0, "& .MuiTypography-root": { fontWeight: selected ? 650 : 500, fontSize: "0.9375rem", lineHeight: 1.45, overflowWrap: "anywhere" } }} />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      );
    };
    return (
      <Box data-testid={isMobile ? "mobile-navigation" : "desktop-navigation"} sx={{ py: 1.5 }}>
        <Toolbar sx={{ display: { xs: "flex", sm: "none" } }}>
          <img
            src="/images/kinnow.png"
            alt="Kinnow"
            style={{ width: "120px", height: "auto" }}
          />
        </Toolbar>
        <Divider
          sx={{ display: { xs: "block", sm: "none" }, borderColor: "#e2e8f0" }}
        />
        {NAVIGATION_GROUPS.map(group => {
          const items = getItems(group.paths);
          const children = (group.children || []).filter(child => getItems(child.paths).length > 0);
          if (!items.length && !children.length) return null;
          const sectionActive = [...items, ...children.flatMap(child => getItems(child.paths))].some(item => item.path === activePath);
          return <Box key={group.id} sx={{ mb: isCollapsed ? 0.75 : 1.25 }}>
            {isCollapsed ? <Divider sx={{ mx: 2, my: 1.25, borderColor: "#eef0f3" }} /> :
              <ListItemButton component="button" onClick={() => toggleGroup(group.id)} aria-expanded={Boolean(expanded[group.id])} aria-controls={`${isMobile ? "mobile" : "desktop"}-${group.id}`}
                sx={{ width: "calc(100% - 24px)", mx: 1.5, minHeight: 56, px: 1.5, borderRadius: "6px", color: sectionActive ? "#344054" : "#475467", "&:hover": { bgcolor: "#f5f6f8" }, "&.Mui-focusVisible": { outline: "2px solid #f97316" } }}>
                <ListItemIcon sx={{ minWidth: 32, color: "#667085", "& .MuiSvgIcon-root": { fontSize: 22 } }}>{SECTION_ICONS[group.id]}</ListItemIcon>
                <Typography sx={{ flex: 1, minWidth: 0, textAlign: "left", fontSize: "1rem", lineHeight: 1.5, fontWeight: 650 }}>{group.label}</Typography>
                <ExpandMoreIcon sx={{ fontSize: 22, transform: expanded[group.id] ? "rotate(180deg)" : "none", transition: reduceMotion ? "none" : "transform 140ms" }} />
              </ListItemButton>}
            <Collapse in={isCollapsed || Boolean(expanded[group.id])} timeout={reduceMotion ? 0 : 160}>
              <Box id={`${isMobile ? "mobile" : "desktop"}-${group.id}`}>
                <List disablePadding>{items.map(item => renderItem(item, item.path === "/instant-orders/history"))}</List>
                {children.map(child => <Box key={child.id}>
                  {!isCollapsed && <ListItemButton component="button" onClick={() => toggleGroup(child.id)} aria-expanded={Boolean(expanded[child.id])} aria-controls={`${isMobile ? "mobile" : "desktop"}-${child.id}`}
                    sx={{ width: "calc(100% - 24px)", mx: 1.5, minHeight: 48, px: 1.5, borderRadius: "6px", color: "#667085", "&:hover": { bgcolor: "#f5f6f8" } }}>
                    <ListItemIcon sx={{ minWidth: 32, mr: 0.5, color: "inherit", justifyContent: "center" }}>{child.id === "staff" ? <SupervisorAccountIcon sx={{ fontSize: 22 }} /> : <SettingsOutlinedIcon sx={{ fontSize: 22 }} />}</ListItemIcon>
                    <Typography sx={{ flex: 1, textAlign: "left", fontSize: "0.9375rem", fontWeight: 500 }}>{child.label}</Typography>
                    <ExpandMoreIcon sx={{ fontSize: 18, transform: expanded[child.id] ? "rotate(180deg)" : "none", transition: reduceMotion ? "none" : "transform 140ms" }} />
                  </ListItemButton>}
                  <Collapse in={isCollapsed || Boolean(expanded[child.id])} timeout={reduceMotion ? 0 : 160}>
                    <List disablePadding id={`${isMobile ? "mobile" : "desktop"}-${child.id}`}>{getItems(child.paths).map(item => renderItem(item, true))}</List>
                  </Collapse>
                </Box>)}
              </Box>
            </Collapse>
          </Box>;
        })}
      </Box>
    );
  };

  return (
    <Box
      component="nav"
      aria-label="Dashboard navigation"
      sx={{ 
        width: { sm: drawerWidth }, 
        flexShrink: { sm: 0 },
        transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)" 
      }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            maxWidth: "calc(100vw - 40px)",
            backgroundColor: "white",
            borderRight: "1px solid #e2e8f0",
            borderRadius: 0,
          },
        }}
      >
        {renderDrawer(true)}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "white",
            borderRight: "1px solid #e2e8f0",
            boxShadow: "none",
            borderRadius: 0,
            top: "64px",
            height: "calc(100vh - 64px)",
            transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            overflowX: "hidden"
          },
        }}
        open
      >
        {renderDrawer(false)}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
